// The frame every editing form renders in.
//
// One place decides the order a form reports trouble in — conflict first (it
// changes what the buttons even mean), then a whole-request failure, then
// per-field messages beside their inputs — so no dialog invents its own
// arrangement and buries the one thing the user has to act on.

import type { ReactNode } from "react";
import styles from "./forms.module.css";
import { Button } from "../design/Button.js";
import { Dialog } from "../design/Dialog.js";
import { Notice, ProblemMessage } from "../design/Feedback.js";
import { ConflictPanel, type ValueFormatter } from "./ConflictPanel.js";
import type { Editor } from "./useEditor.js";

export interface FormDialogProps<TDraft extends object, TRecord> {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One line on WHY this form exists, when that isn't obvious from the title. */
  description?: string;
  submitLabel: string;
  editor: Editor<TDraft, TRecord>;
  /** Field name -> label, for the conflict comparison. */
  labels: Readonly<Record<string, string>>;
  formatValue?: ValueFormatter;
  children: ReactNode;
}

export function FormDialog<TDraft extends object, TRecord>({
  open,
  onClose,
  title,
  description,
  submitLabel,
  editor,
  labels,
  formatValue,
  children,
}: FormDialogProps<TDraft, TRecord>): ReactNode {
  // While a conflict is unresolved the footer's Save is not a meaningful
  // action: the choice belongs to the panel, which spells out what each option
  // does to the other person's change.
  const conflictBlocks = editor.conflict !== null && editor.conflict.phase !== "reload_failed";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      onSubmit={editor.submit}
      footer={
        <>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={editor.isSaving || conflictBlocks}>
            {editor.isSaving ? "Saving…" : submitLabel}
          </Button>
        </>
      }
    >
      {description !== undefined && <p className={styles.description}>{description}</p>}

      {editor.conflict !== null && (
        <ConflictPanel
          conflict={editor.conflict}
          labels={labels}
          {...(formatValue === undefined ? {} : { formatValue })}
          onReload={editor.reloadCurrent}
          onKeepMine={editor.keepMine}
          onUseTheirs={editor.useTheirs}
        />
      )}

      {editor.failure !== null && (
        <ProblemMessage error={editor.failure} action={`save this ${title.toLowerCase()}`} />
      )}

      {editor.unmappedErrors.length > 0 && (
        <Notice tone="warn" title="The API rejected some values.">
          {editor.unmappedErrors.map((error) => (
            <div key={`${error.field}:${error.code}`}>{`${error.field}: ${error.message}`}</div>
          ))}
        </Notice>
      )}

      {children}
    </Dialog>
  );
}
